import React, { useEffect, useState, useMemo } from 'react';
import AdminLayout from '@/components/AdminLayout';
import api, { apiErrorMessage } from '@/lib/api';
import { toast } from 'sonner';
import { Loader2, Plus, Edit3, Trash2, Save, X, Award, Search, LayoutGrid, AlertTriangle } from 'lucide-react';

const VIOLET = '#7C6BEF';
const RED = '#DC2626';
const CATEGORIES = ['Seguridad', 'Salud Ocupacional', 'Calidad', 'Medio Ambiente', 'Cumplimiento Normativo'];

/** Iter 50 · Admin CRUD for the commercial "distintivos" catalog.
 * A distintivo bundles N sections from the master checklist. */
export default function AdminDistintivos() {
    const [rows, setRows] = useState(null);
    const [sections, setSections] = useState([]);
    const [q, setQ] = useState('');
    const [editing, setEditing] = useState(null); // 'new' | doc | null
    const [showMatrix, setShowMatrix] = useState(false);

    const load = async () => {
        try {
            const [d, tpl] = await Promise.all([
                api.get('/distintivos'),
                api.get('/checklist-template'),
            ]);
            setRows(d.data || []);
            setSections((tpl.data?.sections || []).map((s) => ({ id: s.section_id, title: s.title })));
        } catch (e) {
            toast.error(apiErrorMessage(e, 'Error al cargar distintivos'));
            setRows([]);
        }
    };
    useEffect(() => { load(); }, []);

    const filtered = useMemo(() => {
        if (!q.trim()) return rows || [];
        const t = q.toLowerCase();
        return (rows || []).filter((r) =>
            (r.code || '').toLowerCase().includes(t)
            || (r.name || '').toLowerCase().includes(t)
            || (r.category || '').toLowerCase().includes(t),
        );
    }, [rows, q]);

    const save = async (form) => {
        try {
            if (editing === 'new') {
                await api.post('/distintivos', form);
                toast.success('Distintivo creado');
            } else {
                await api.patch(`/distintivos/${editing.id}`, form);
                toast.success('Distintivo actualizado');
            }
            setEditing(null);
            await load();
        } catch (e) { toast.error(apiErrorMessage(e, 'Error al guardar')); throw e; }
    };

    const del = async (dist) => {
        if (!window.confirm(`Eliminar el distintivo "${dist.name}"? Esta acción no borra las respuestas de auditorías, pero remueve el paquete comercial y sus tags en las secciones.`)) return;
        try {
            await api.delete(`/distintivos/${dist.id}`);
            toast.success('Distintivo eliminado');
            await load();
        } catch (e) { toast.error(apiErrorMessage(e, 'Error al eliminar')); }
    };

    if (rows === null) {
        return <AdminLayout title="Distintivos"><div className="py-20 text-center text-gray-500"><Loader2 className="w-8 h-8 animate-spin mx-auto mb-3" /> Cargando…</div></AdminLayout>;
    }

    return (
        <AdminLayout title="Distintivos">
            <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.2em] font-bold mb-1 text-gray-500">Catálogo comercial</div>
                    <h1 className="font-display text-3xl font-black tracking-tight">Distintivos</h1>
                    <p className="text-sm text-gray-500 mt-1 max-w-2xl">
                        Cada distintivo agrupa un conjunto de secciones del checklist maestro. El cliente selecciona uno o varios al solicitar; al cerrar la auditoría se emite un PDF por distintivo aprobado.
                    </p>
                </div>
                <button onClick={() => setEditing('new')}
                    data-testid="new-distintivo-btn"
                    className="inline-flex items-center gap-2 px-4 py-2 text-white text-sm font-bold"
                    style={{ background: VIOLET }}>
                    <Plus className="w-4 h-4" /> Nuevo distintivo
                </button>
            </div>

            <div className="mb-4 flex items-center gap-2 flex-wrap">
                <button
                    onClick={() => setShowMatrix(true)}
                    data-testid="coverage-matrix-btn"
                    className="inline-flex items-center gap-2 px-3 py-2 border text-xs font-bold hover:bg-black/5 dark:hover:bg-white/5"
                    style={{ borderColor: 'var(--border)' }}
                    title="Ver cobertura efectiva por distintivo — detecta huecos de scoring"
                >
                    <LayoutGrid className="w-3.5 h-3.5" style={{ color: VIOLET }} />
                    Ver matriz de cobertura
                </button>
            </div>

            <div className="mb-4 flex items-center gap-2 px-3 py-2 border" style={{ borderColor: 'var(--border)' }}>
                <Search className="w-4 h-4 opacity-50" />
                <input value={q} onChange={(e) => setQ(e.target.value)}
                    placeholder="Buscar por código, nombre o categoría…"
                    data-testid="distintivo-search"
                    className="flex-1 bg-transparent outline-none text-sm" />
            </div>

            {filtered.length === 0 ? (
                <div className="p-12 border text-center text-sm text-gray-500" style={{ borderColor: 'var(--border)' }} data-testid="distintivo-empty">
                    <Award className="w-10 h-10 mx-auto mb-3 opacity-40" />
                    Sin distintivos que coincidan.
                </div>
            ) : (
                <div className="border overflow-x-auto" style={{ borderColor: 'var(--border)' }}>
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left uppercase font-bold text-gray-500 text-[10px] border-b" style={{ borderColor: 'var(--border)' }}>
                                <th className="py-3 px-4">Código</th>
                                <th className="py-3 px-4">Nombre</th>
                                <th className="py-3 px-4">Categoría</th>
                                <th className="py-3 px-4">Secciones</th>
                                <th className="py-3 px-4">Umbral</th>
                                <th className="py-3 px-4">Estado</th>
                                <th className="py-3 px-4 w-0"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((r) => (
                                <tr key={r.id} className="border-b" style={{ borderColor: 'var(--border)' }} data-testid={`distintivo-row-${r.code}`}>
                                    <td className="py-3 px-4 font-mono font-bold">{r.code}</td>
                                    <td className="py-3 px-4">{r.name}</td>
                                    <td className="py-3 px-4 text-xs">{r.category}</td>
                                    <td className="py-3 px-4 text-xs">{(r.checklist_section_ids || []).length}</td>
                                    <td className="py-3 px-4 text-xs">{r.default_approval_threshold}%</td>
                                    <td className="py-3 px-4">
                                        <span className="inline-block px-2 py-0.5 text-[10px] font-bold uppercase"
                                            style={{ background: r.is_active ? '#10B98122' : '#DC262622', color: r.is_active ? '#10B981' : '#DC2626' }}>
                                            {r.is_active ? 'Activo' : 'Inactivo'}
                                        </span>
                                    </td>
                                    <td className="py-3 px-4 whitespace-nowrap">
                                        <button onClick={async () => {
                                            try {
                                                const rsp = await api.get(`/admin/distintivos/${r.id}/sample-pdf`, { responseType: 'blob' });
                                                const url = URL.createObjectURL(new Blob([rsp.data], { type: 'application/pdf' }));
                                                window.open(url, '_blank');
                                                setTimeout(() => URL.revokeObjectURL(url), 20000);
                                            } catch (e) { toast.error(apiErrorMessage(e, 'Error al generar muestra')); }
                                        }} className="p-1.5 hover:bg-black/5 dark:hover:bg-white/5" data-testid={`sample-pdf-${r.code}`} title="Ver PDF muestra">
                                            <Award className="w-4 h-4" />
                                        </button>
                                        <button onClick={() => setEditing(r)} className="p-1.5 hover:bg-black/5 dark:hover:bg-white/5" data-testid={`edit-distintivo-${r.code}`} title="Editar">
                                            <Edit3 className="w-4 h-4" />
                                        </button>
                                        <button onClick={() => del(r)} className="p-1.5 hover:bg-black/5 dark:hover:bg-white/5 text-red-600" data-testid={`delete-distintivo-${r.code}`} title="Eliminar">
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {editing && (
                <DistintivoEditor
                    initial={editing === 'new' ? null : editing}
                    sections={sections}
                    onSave={save}
                    onClose={() => setEditing(null)}
                />
            )}

            {showMatrix && (
                <CoverageMatrixModal onClose={() => setShowMatrix(false)} />
            )}
        </AdminLayout>
    );
}


/**
 * Iter 60 · Fase C · Coverage matrix
 * Shows, per distintivo, how many items are effectively in scope vs. total,
 * and flags any section with a 0-item coverage gap.
 */
function CoverageMatrixModal({ onClose }) {
    const [data, setData] = useState(null);
    const [expanded, setExpanded] = useState({}); // { distintivo_id: boolean }

    useEffect(() => {
        api.get('/admin/distintivos/coverage-matrix')
            .then((r) => setData(r.data || []))
            .catch((e) => {
                toast.error(apiErrorMessage(e, 'Error al cargar la matriz'));
                setData([]);
            });
    }, []);

    const hasAnyGap = (data || []).some((d) => (d.coverage_gaps || []).length > 0);

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
            <div className="bg-white dark:bg-neutral-900 border max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col" style={{ borderColor: 'var(--border)' }} onClick={(e) => e.stopPropagation()} data-testid="coverage-matrix-modal">
                <div className="px-5 py-4 border-b flex items-center justify-between" style={{ borderColor: 'var(--border)' }}>
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">Iter 60 · Auditoría de cobertura</div>
                        <h2 className="font-display text-xl font-black tracking-tight flex items-center gap-2">
                            <LayoutGrid className="w-5 h-5" style={{ color: VIOLET }} />
                            Matriz de cobertura por Distintivo
                        </h2>
                    </div>
                    <button onClick={onClose} data-testid="coverage-matrix-close" className="p-1.5 hover:bg-black/5 dark:hover:bg-white/5">
                        <X className="w-5 h-5" />
                    </button>
                </div>
                <div className="px-5 py-3 border-b text-xs opacity-70" style={{ borderColor: 'var(--border)' }}>
                    Resolución efectiva: cada ítem se cuenta bajo el distintivo si su
                    <span className="font-mono px-1">distintivos_incluidos</span>
                    lo incluye directamente (override), o si su sección padre lo incluye y el ítem hereda.
                </div>

                {hasAnyGap && data && data.length > 0 && (
                    <div className="mx-5 mt-3 flex items-start gap-2 px-3 py-2 border-l-4" style={{ background: '#DC262611', borderColor: RED }}>
                        <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" style={{ color: RED }} />
                        <div className="text-xs">
                            <span className="font-bold" style={{ color: RED }}>Hay distintivos con secciones sin cobertura</span> — revisa las filas marcadas abajo antes de comercializar el paquete.
                        </div>
                    </div>
                )}

                <div className="flex-1 overflow-y-auto p-5 min-h-0">
                    {data === null ? (
                        <div className="text-center py-12 opacity-60"><Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Cargando matriz…</div>
                    ) : data.length === 0 ? (
                        <div className="text-center py-12 opacity-60">No hay distintivos registrados aún.</div>
                    ) : (
                        <div className="space-y-2">
                            {data.map((d) => {
                                const gap = (d.coverage_gaps || []).length > 0;
                                const pct = d.items_total > 0 ? Math.round((d.items_in_scope / d.items_total) * 100) : 0;
                                const isOpen = !!expanded[d.distintivo_id];
                                return (
                                    <div
                                        key={d.distintivo_id}
                                        className="border"
                                        style={{ borderColor: gap ? `${RED}55` : 'var(--border)', background: gap ? '#DC262608' : 'transparent' }}
                                        data-testid={`coverage-row-${d.code}`}
                                    >
                                        <button
                                            type="button"
                                            className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-black/[0.02] dark:hover:bg-white/5"
                                            onClick={() => setExpanded((s) => ({ ...s, [d.distintivo_id]: !isOpen }))}
                                            data-testid={`coverage-expand-${d.code}`}
                                        >
                                            <span className="font-mono text-xs font-bold shrink-0 px-2 py-0.5" style={{ background: `${VIOLET}22`, color: VIOLET }}>{d.code}</span>
                                            <span className="flex-1 min-w-0 truncate text-sm">{d.name}</span>
                                            <span className="text-[10px] uppercase tracking-wider opacity-60 shrink-0">{d.category}</span>
                                            <span className="text-[10px] uppercase tracking-wider font-bold opacity-70 shrink-0">
                                                {d.sections_count} sec
                                            </span>
                                            <span
                                                className="text-xs font-bold shrink-0 px-2 py-1"
                                                style={{
                                                    background: gap ? `${RED}22` : pct === 100 ? '#10B98122' : `${VIOLET}22`,
                                                    color: gap ? RED : pct === 100 ? '#10B981' : VIOLET,
                                                }}
                                                data-testid={`coverage-total-${d.code}`}
                                            >
                                                {d.items_in_scope}/{d.items_total} ítems · {pct}%
                                            </span>
                                            {gap && (
                                                <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-1 shrink-0" style={{ background: `${RED}22`, color: RED }} data-testid={`coverage-gap-badge-${d.code}`}>
                                                    <AlertTriangle className="w-3 h-3" /> {(d.coverage_gaps || []).length} hueco(s)
                                                </span>
                                            )}
                                        </button>
                                        {isOpen && (
                                            <div className="border-t px-4 py-3" style={{ borderColor: 'var(--border)' }}>
                                                {(d.sections || []).length === 0 ? (
                                                    <div className="text-xs italic opacity-60">Este distintivo no tiene secciones asignadas.</div>
                                                ) : (
                                                    <table className="w-full text-xs">
                                                        <thead>
                                                            <tr className="text-left uppercase font-bold text-[10px] text-gray-500 border-b" style={{ borderColor: 'var(--border)' }}>
                                                                <th className="py-2 pr-2">Sección</th>
                                                                <th className="py-2 pr-2">Título</th>
                                                                <th className="py-2 pr-2 text-right">Ítems</th>
                                                                <th className="py-2 pr-2 text-right">En scope</th>
                                                                <th className="py-2 pr-2 text-right">%</th>
                                                                <th className="py-2 pr-2"></th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {(d.sections || []).map((s) => {
                                                                const secPct = s.items_total > 0 ? Math.round((s.items_in_scope / s.items_total) * 100) : 0;
                                                                return (
                                                                    <tr key={s.section_id} className="border-b" style={{ borderColor: 'var(--border)' }}>
                                                                        <td className="py-1.5 pr-2 font-mono">{s.section_id}</td>
                                                                        <td className="py-1.5 pr-2">{s.title}</td>
                                                                        <td className="py-1.5 pr-2 text-right opacity-70">{s.items_total}</td>
                                                                        <td className="py-1.5 pr-2 text-right font-bold">{s.items_in_scope}</td>
                                                                        <td className="py-1.5 pr-2 text-right">{secPct}%</td>
                                                                        <td className="py-1.5 pr-2">
                                                                            {s.coverage_gap && (
                                                                                <span className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5" style={{ background: `${RED}22`, color: RED }} title="Ningún ítem de esta sección está en scope">
                                                                                    <AlertTriangle className="w-2.5 h-2.5" /> gap
                                                                                </span>
                                                                            )}
                                                                        </td>
                                                                    </tr>
                                                                );
                                                            })}
                                                        </tbody>
                                                    </table>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
                <div className="px-5 py-3 border-t flex justify-end gap-2" style={{ borderColor: 'var(--border)' }}>
                    <button onClick={onClose} className="px-4 py-2 border text-sm font-bold" style={{ borderColor: 'var(--border)' }}>Cerrar</button>
                </div>
            </div>
        </div>
    );
}


function DistintivoEditor({ initial, sections, onSave, onClose }) {
    const [busy, setBusy] = useState(false);
    const [standards, setStandards] = useState([]);   // Iter 63bis · para default_standard_id
    useEffect(() => { api.get('/standards').then((r) => setStandards(r.data || [])).catch(() => {}); }, []);
    const [form, setForm] = useState(() => ({
        code: initial?.code || '',
        name: initial?.name || '',
        marketing_description: initial?.marketing_description || '',
        category: initial?.category || 'Cumplimiento Normativo',
        is_active: initial?.is_active !== undefined ? initial.is_active : true,
        coming_soon: !!initial?.coming_soon,
        default_standard_id: initial?.default_standard_id || '',
        checklist_section_ids: initial?.checklist_section_ids || [],
        default_approval_threshold: initial?.default_approval_threshold ?? 80,
        cover_image_url: initial?.cover_image_url || '',
    }));

    const toggleSection = (sid) => {
        setForm((p) => ({
            ...p,
            checklist_section_ids: p.checklist_section_ids.includes(sid)
                ? p.checklist_section_ids.filter((x) => x !== sid)
                : [...p.checklist_section_ids, sid],
        }));
    };
    const allOn = form.checklist_section_ids.length === sections.length;
    const submit = async () => {
        setBusy(true);
        try { await onSave(form); } catch { /* handled upstream */ } finally { setBusy(false); }
    };
    const disabled = busy || !form.code.trim() || !form.name.trim();

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/60" onClick={onClose} />
            <div className="relative w-full max-w-3xl shadow-2xl flex flex-col max-h-[90vh]" style={{ background: 'var(--card-bg)' }}>
                <div className="p-5 border-b flex items-center justify-between gap-3 shrink-0" style={{ borderColor: 'var(--border)' }}>
                    <div className="font-display font-black text-lg">{initial ? `Editar · ${initial.code}` : 'Nuevo distintivo'}</div>
                    <button onClick={onClose} className="p-2 hover:bg-black/5 dark:hover:bg-white/5"><X className="w-5 h-5" /></button>
                </div>
                <div className="p-5 overflow-y-auto flex-1 min-h-0 space-y-4">
                    <div className="grid md:grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs uppercase tracking-wider font-bold text-gray-500 mb-1">Código</label>
                            <input type="text" value={form.code}
                                onChange={(e) => setForm({ ...form, code: e.target.value })}
                                data-testid="dist-code-input"
                                placeholder="AIGIS-SAFE"
                                className="w-full px-3 py-2 border-2 outline-none text-sm font-mono"
                                style={{ borderColor: 'var(--border)', background: 'var(--card-bg)' }} />
                        </div>
                        <div>
                            <label className="block text-xs uppercase tracking-wider font-bold text-gray-500 mb-1">Categoría</label>
                            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                                data-testid="dist-category-input"
                                className="w-full px-3 py-2 border-2 outline-none text-sm"
                                style={{ borderColor: 'var(--border)', background: 'var(--card-bg)' }}>
                                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </div>
                    </div>
                    <div>
                        <label className="block text-xs uppercase tracking-wider font-bold text-gray-500 mb-1">Nombre comercial</label>
                        <input type="text" value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            data-testid="dist-name-input"
                            placeholder="Distintivo AIGIS Safe"
                            className="w-full px-3 py-2 border-2 outline-none text-sm"
                            style={{ borderColor: 'var(--border)', background: 'var(--card-bg)' }} />
                    </div>
                    <div>
                        <label className="block text-xs uppercase tracking-wider font-bold text-gray-500 mb-1">Descripción de marketing</label>
                        <textarea value={form.marketing_description}
                            onChange={(e) => setForm({ ...form, marketing_description: e.target.value })}
                            data-testid="dist-desc-input"
                            rows={3}
                            className="w-full px-3 py-2 border-2 outline-none text-sm"
                            style={{ borderColor: 'var(--border)', background: 'var(--card-bg)' }} />
                    </div>
                    <div className="grid md:grid-cols-3 gap-3">
                        <div>
                            <label className="block text-xs uppercase tracking-wider font-bold text-gray-500 mb-1">Umbral aprobación (%)</label>
                            <input type="number" min="0" max="100" step="1" value={form.default_approval_threshold}
                                onChange={(e) => setForm({ ...form, default_approval_threshold: parseFloat(e.target.value) })}
                                data-testid="dist-threshold-input"
                                className="w-full px-3 py-2 border-2 outline-none text-sm"
                                style={{ borderColor: 'var(--border)', background: 'var(--card-bg)' }} />
                        </div>
                        {/* Iter 63bis · norma por default que hereda la auditoría cuando el admin agenda por multi-select */}
                        <div className="md:col-span-2">
                            <label className="block text-xs uppercase tracking-wider font-bold text-gray-500 mb-1">
                                Norma por defecto <span className="opacity-60 font-normal">(auto-derivada al crear auditoría)</span>
                            </label>
                            <select value={form.default_standard_id || ''}
                                onChange={(e) => setForm({ ...form, default_standard_id: e.target.value })}
                                data-testid="dist-default-standard-input"
                                className="w-full px-3 py-2 border-2 outline-none text-sm"
                                style={{ borderColor: 'var(--border)', background: 'var(--card-bg)' }}>
                                <option value="">— Sin norma por defecto —</option>
                                {standards.map((s) => (
                                    <option key={s.id} value={s.id}>{s.code} — {s.name}</option>
                                ))}
                            </select>
                        </div>
                        <div className="md:col-span-3 flex items-end gap-4 flex-wrap">
                            <label className="flex items-center gap-2 text-sm cursor-pointer">
                                <input type="checkbox" checked={form.is_active}
                                    onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                                    data-testid="dist-active-input" />
                                <span>Activo (visible en el catálogo público)</span>
                            </label>
                            {/* Iter 63 · coming_soon flag */}
                            <label className="flex items-center gap-2 text-sm cursor-pointer" title="Aparece en listados públicos pero no puede solicitarse hasta que se desactive">
                                <input type="checkbox" checked={form.coming_soon}
                                    onChange={(e) => setForm({ ...form, coming_soon: e.target.checked })}
                                    data-testid="dist-coming-soon-input" />
                                <span>Próximamente <span className="text-[10px] opacity-60">(bloquea solicitudes)</span></span>
                            </label>
                        </div>
                    </div>

                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <label className="block text-xs uppercase tracking-wider font-bold text-gray-500">Secciones del checklist agrupadas ({form.checklist_section_ids.length} / {sections.length})</label>
                            <button type="button" onClick={() => setForm({ ...form, checklist_section_ids: allOn ? [] : sections.map((s) => s.id) })}
                                data-testid="dist-toggle-all"
                                className="text-[11px] font-bold uppercase" style={{ color: VIOLET }}>
                                {allOn ? 'Deseleccionar todas' : 'Seleccionar todas'}
                            </button>
                        </div>
                        <div className="border max-h-64 overflow-y-auto" style={{ borderColor: 'var(--border)' }} data-testid="dist-sections-list">
                            {sections.map((s) => (
                                <label key={s.id} className="flex items-center gap-2 px-3 py-2 border-b text-sm cursor-pointer hover:bg-black/[0.02] dark:hover:bg-white/5"
                                    style={{ borderColor: 'var(--border)' }}
                                    data-testid={`dist-section-${s.id}`}>
                                    <input type="checkbox" checked={form.checklist_section_ids.includes(s.id)} onChange={() => toggleSection(s.id)} />
                                    <span className="font-mono text-xs opacity-60">{s.id}</span>
                                    <span className="flex-1">{s.title}</span>
                                </label>
                            ))}
                        </div>
                    </div>
                </div>
                <div className="p-4 border-t flex gap-2 shrink-0" style={{ borderColor: 'var(--border)', background: 'var(--card-bg)' }}>
                    <button onClick={submit} disabled={disabled}
                        data-testid="dist-save"
                        className="flex-1 text-white px-5 py-2.5 font-bold inline-flex items-center justify-center gap-2 disabled:opacity-40"
                        style={{ background: VIOLET }}>
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                        {initial ? 'Guardar cambios' : 'Crear distintivo'}
                    </button>
                    <button onClick={onClose} className="px-5 py-2.5 border text-sm font-medium" style={{ borderColor: 'var(--border)' }}>Cancelar</button>
                </div>
            </div>
        </div>
    );
}
